<script setup lang="ts">
import { onMounted, ref, toRefs, watch } from "vue";
import { nanoid } from "nanoid";
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import type { DefaultInputProps } from "@/interfaces";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import DatePickerModal from "@/stories/form/DatePicker/DatePickerModal.vue";
import { type IDatePickerContext } from "@/stories/form/DatePicker/shared";
import { toDate, toUnix } from "@/stories/utils/dates";
import { getDate, getMonth, getYear } from "date-fns";
import { useDates } from "@/stories/utils/useDates";

const id = nanoid();

const CURRENT_YEAR = new Date().getFullYear();

const CURRENT_MONTH = new Date().getMonth();

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  modelValue?: number;
  icon?: boolean;
  hideValidationError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  icon: true,
  hideValidationError: false,
});

const { validation, name } = toRefs(props);

const isSelecboxOpen = ref<boolean>(false);
const context = ref<IDatePickerContext>({
  view: {
    year: CURRENT_YEAR,
    page: CURRENT_YEAR,
    month: CURRENT_MONTH,
    day: new Date().getDate(),
    type: "days",
  },
  selection: null,
  model: null,
});

const parseValue = (emitValue: boolean = true) => {
  context.value.model = getModelByValue(props.modelValue);
  if (context.value.model) {
    context.value.selection = context.value.model;
    context.value.view = {
      ...context.value.view,
      ...context.value.model,
    };
  }
  if (emitValue) {
    handleInput(context.value.model?.asUnix || null);
  }
};

// We should set the internal context if there is any value
onMounted(() => parseValue(false));

// We should update the internal context when the value is changed
watch(
  () => props.modelValue,
  () => parseValue(),
);

const emit = defineEmits(["update:modelValue"]);

const setView = (view: any) => {
  context.value.view = view;
};

const setSelection = (selection: any) => {
  context.value.selection = selection;
};

const handleSave = (model: any) => {
  context.value.model = model;
  handleInput(context.value.model?.asUnix || null);
  isSelecboxOpen.value = false;
};

const openView = () => {
  isSelecboxOpen.value = true;

  if (context.value.model) {
    context.value.selection = {
      year: context.value.model.year,
      month: context.value.model.month,
      day: context.value.model.day,
    };
    context.value.view = {
      ...context.value.view,
      year: context.value.model.year,
      month: context.value.model.month,
      day: context.value.model.day,
      type: "days",
    };
  } else {
    context.value.view = {
      ...context.value.view,
      year: CURRENT_YEAR,
      month: CURRENT_MONTH,
      day: new Date().getDate(),
      type: "days",
    };
  }
};

const handleInput = (value: number | null) => {
  emit("update:modelValue", value);

  if (validation.value && validation.value.errors) {
    delete validation.value.errors[name.value];
  }
};

const { hasError } = useFieldValidationError(name, validation);

const { format } = useDates();

const getModelByValue = (model: any) => {
  if (!model) {
    return null;
  }

  const date = toDate(model);
  return {
    year: getYear(date),
    month: getMonth(date),
    day: getDate(date),
    asString: format("long-date", date),
    asUnix: toUnix(date),
  };
};
</script>

<template>
  <div>
    <InputWrapper
      :label="label"
      :forId="id"
      :inputKey="inputKey"
      :tooltip="tooltip"
      :is-required="isRequired"
      :disabled="disabled"
      :description="description"
      :name="name"
      :validation="validation"
      :errorMessageKey="errorMessageKey"
      :hide-validation-error="hideValidationError"
    >
      <button
        :id="id"
        type="button"
        class="transition duration-200 text-base w-full text-neutral1000 border border-neutral500 placeholder:text-neutral600 placeholder:font-normal placeholder:tracking-tight focus:outline-2 focus:enabled:outline-neutral1000 disabled:text-neutral600 bg-neutral0 p-4 rounded-lg text-left focusVisible h-[48px] flex items-center gap-3 disabled:bg-neutral50 b300"
        :class="{
          'outline outline-2 outline-red400': hasError,
          'hover:enabled:outline-neutral500': !hasError,
        }"
        :placeholder="placeholder"
        :disabled="disabled"
        @click="openView"
      >
        <i v-if="icon" class="far fa-calendar"></i>
        {{ context.model?.asString }}
      </button>
    </InputWrapper>
    <DatePickerModal
      :is-selecbox-open="isSelecboxOpen"
      :view="context.view"
      :selection="context.selection"
      @close="isSelecboxOpen = false"
      @setView="setView"
      @setSelection="setSelection"
      @handleSave="handleSave"
    />
  </div>
</template>

<style lang="scss"></style>
